import { formatJobDates, formatJobPay, jobHasEnded, vacancyFreshness } from "@/lib/marketplace/job-terms";
import { EligibilitySummary } from "./EligibilitySummary";
import type { EligibilityProfile } from "@/lib/marketplace/eligibility";
import Image from "next/image";
import Link from "next/link";
import type { MouseEvent, Ref } from "react";
import { VerifiedBadge } from "@/app/components/VerifiedBadge";
import type { UserRole } from "@/lib/auth";
import type { ListingWithCompany } from "@/lib/marketplace/listing-types";
import { SEASON_TYPES } from "@/lib/seasons";
import {
  LISTING_PERKS,
  LOCATIONS_TREE,
  ROLES_TREE,
  findLabelById,
} from "@/lib/taxonomy";

export interface ListingCardProps {
  eligibilityProfile?: EligibilityProfile;
  listing: ListingWithCompany;
  userRole: UserRole;
  isInterested: boolean;
  isSaved: boolean;
  conversationId?: string;
  onInterestClick: (event: MouseEvent<HTMLButtonElement>) => void;
  onMessageClick: (event: MouseEvent<HTMLButtonElement>) => void;
  onSaveClick: () => void;
  onListingDetailClick: (navigate?: boolean) => void;
  isLoading: boolean;
  isSaveLoading: boolean;
  matchReasons: string[];
  interestSuccessRef?: Ref<HTMLSpanElement>;
  conversationSuccessRef?: Ref<HTMLAnchorElement>;
  actionsDisabled?: boolean;
}

function postedTime(dateStr: string): string {
  const days = Math.floor(
    (Date.now() - new Date(dateStr).getTime()) / (24 * 60 * 60 * 1000),
  );
  if (days === 0) return "Posted today";
  if (days === 1) return "Posted 1d ago";
  if (days < 7) return `Posted ${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `Posted ${weeks}w ago`;
  const months = Math.floor(days / 30);
  return `Posted ${months}mo ago`;
}

function getSeasonBadgeStyle(displayGroup: string): {
  bg: string;
  color: string;
} {
  switch (displayGroup) {
    case "snow":
      return { bg: "#E8EBF0", color: "#1B2A4A" };
    case "sun":
      return { bg: "#E8F7FE", color: "#0EA5E9" };
    case "festival":
      return { bg: "#FDF0EB", color: "#E8835C" };
    default:
      return { bg: "#F0F0F0", color: "#3A3A3A" };
  }
}

export function ListingCard({
  listing,
  eligibilityProfile,
  userRole,
  isInterested,
  isSaved,
  conversationId,
  onInterestClick,
  onMessageClick,
  onSaveClick,
  onListingDetailClick,
  isLoading,
  isSaveLoading,
  matchReasons,
  interestSuccessRef,
  conversationSuccessRef,
  actionsDisabled = false,
}: ListingCardProps) {
  const seasonType = SEASON_TYPES.find((item) => item.id === listing.season_type);
  const roleLabels = listing.roles
    .map((role) => findLabelById(ROLES_TREE, role))
    .filter(Boolean)
    .slice(0, 3);
  const locationLabels = listing.locations
    .map((location) => findLabelById(LOCATIONS_TREE, location))
    .filter(Boolean);
  const visibleLocations = locationLabels.slice(0, 3);
  const remainingRoles = listing.roles.length - 3;
  const remainingLocations = locationLabels.length - 3;
  const company = listing.company;
  const initials = company.name
    .split(/\s+/)
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const seasonAccent = seasonType
    ? {
        snow: "#1B2A4A",
        sun: "#0EA5E9",
        festival: "#E8835C",
      }[seasonType.displayGroup] ?? "#1B2840"
    : "#1B2840";
  const accentColor = company.brand_color || seasonAccent;
  const perkEntries = listing.perks
    .map((id) => LISTING_PERKS.find((perk) => perk.id === id))
    .filter(Boolean) as (typeof LISTING_PERKS)[number][];
  const visiblePerks = perkEntries.slice(0, 2);
  const remainingPerks = perkEntries.length - 2;
  const showCandidateCtas =
    userRole === "candidate" && listing.status === "active" && !jobHasEnded(listing);
  const showVisitorCtas = !userRole && listing.status === "active" && !jobHasEnded(listing);

  function handleDetailLinkClick(event: MouseEvent<HTMLAnchorElement>) {
    event.stopPropagation();
    onListingDetailClick(false);
  }

  return (
    <div
      className={`bg-white rounded-[16px] border border-border-light overflow-hidden transition-all duration-200 flex flex-col ${actionsDisabled ? "cursor-default" : "cursor-pointer hover:border-gold/30 hover:shadow-[0_4px_20px_rgba(0,0,0,0.06)] hover:-translate-y-0.5"}`}
      onClick={actionsDisabled ? undefined : (event) => {
        if (event.target instanceof Element && event.target.closest("a, button, input, select, textarea")) return;
        onListingDetailClick();
      }}
    >
      <div className="px-4 py-4 sm:px-5 sm:py-4 flex-1 flex flex-col">
        <div className="flex items-center gap-3.5">
          {company.logo_url ? (
            <Image
              src={company.logo_url}
              alt=""
              width={48}
              height={48}
              sizes="48px"
              className="w-12 h-12 rounded-[12px] object-cover shrink-0"
            />
          ) : (
            <div
              className="w-12 h-12 rounded-[12px] flex items-center justify-center text-white text-[16px] font-bold shrink-0"
              style={{
                background: `linear-gradient(135deg, ${accentColor}, ${accentColor}dd)`,
              }}
              aria-hidden="true"
            >
              {initials}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <span className="text-[14px] font-semibold text-charcoal block">
              {company.name}
            </span>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              {company.verified && <VerifiedBadge />}
              {seasonType && (
                <span
                  className="inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold"
                  style={{
                    backgroundColor: getSeasonBadgeStyle(seasonType.displayGroup).bg,
                    color: getSeasonBadgeStyle(seasonType.displayGroup).color,
                  }}
                >
                  {seasonType.label}
                </span>
              )}
              {company.location && (
                <span className="text-[12px] text-text-muted/70">
                  {company.location}
                </span>
              )}
            </div>
          </div>
        </div>

        <p className="text-[11px] text-text-muted mt-2 mb-1.5">
          {postedTime(listing.created_at)}
          {listing.interest_count >= 3 &&
            ` \u00B7 ${listing.interest_count} interested`}
        </p>

        <h3 className="text-[15px] font-bold text-navy mb-1.5 line-clamp-2 leading-snug">
          {actionsDisabled ? (
            <span className="text-navy">{listing.title}</span>
          ) : (
            <Link
              href={`/jobs/${listing.id}`}
              onClick={handleDetailLinkClick}
              className="text-navy no-underline hover:text-gold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
            >
              {listing.title}
            </Link>
          )}
        </h3>

        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-text-muted mb-2">
          <span>
            {formatJobDates(listing)}
          </span>
        </div>
        <p className="mb-2 text-xs font-medium text-charcoal">{formatJobPay(listing.job_terms)}</p>
        {listing.pay_info && <p className="mb-2 text-xs text-text-muted">Employer wording: {listing.pay_info}</p>}
        <div className="mb-3 text-xs text-text-muted">
          {vacancyFreshness(listing)}
        </div>

        {roleLabels.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            {roleLabels.map((role) => (
              <span
                key={role}
                className="px-2.5 py-1 rounded-sm bg-[#EEF2F7] text-[#4A5568] text-[11px] font-medium"
              >
                {role}
              </span>
            ))}
            {remainingRoles > 0 && (
              <span className="px-2.5 py-1 rounded-sm bg-[#EEF2F7] text-text-muted text-[11px] font-medium">
                +{remainingRoles}
              </span>
            )}
          </div>
        )}

        {visibleLocations.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            {visibleLocations.map((location) => (
              <span
                key={location}
                className="px-2.5 py-1 rounded-sm bg-gold/[0.08] text-gold text-[11px] font-medium"
              >
                {location}
              </span>
            ))}
            {remainingLocations > 0 && (
              <span className="px-2.5 py-1 rounded-sm bg-gold/[0.08] text-text-muted text-[11px] font-medium">
                +{remainingLocations} more
              </span>
            )}
          </div>
        )}

        {visiblePerks.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {visiblePerks.map((perk) => (
              <span
                key={perk.id}
                className="px-2.5 py-1 rounded-sm bg-[#E8F5E9] text-[#2E7D32] text-[10px] font-medium"
              >
                {perk.label}
              </span>
            ))}
            {remainingPerks > 0 && (
              <span className="px-2.5 py-1 rounded-sm bg-[#E8F5E9] text-text-muted text-[10px] font-medium">
                +{remainingPerks} more
              </span>
            )}
          </div>
        )}

        <EligibilitySummary requirements={listing.eligibility_requirements} profile={eligibilityProfile} startDate={listing.start_date} candidate={userRole === "candidate"} />

        {matchReasons.length > 0 && (
          <div className="rounded-[10px] bg-warm-bg border border-border-light px-3 py-2.5 mb-2">
            <p className="text-[11px] font-semibold text-navy mb-1">
              Why it might fit
            </p>
            <ul className="space-y-0.5">
              {matchReasons.slice(0, 2).map((reason) => (
                <li key={reason} className="text-[11px] text-text-muted leading-snug">
                  {reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div
          className="pt-3 mt-auto border-t border-border-light"
          onClick={(event) => event.stopPropagation()}
        >
          {userRole !== "employer" && listing.status === "active" && !jobHasEnded(listing) && (
            <button
              type="button"
              onClick={onSaveClick}
              disabled={actionsDisabled || isSaveLoading}
              className={`mb-2 min-h-[44px] w-full px-4 py-2 rounded-sm text-[13px] font-semibold border-[1.5px] cursor-pointer font-sans transition-all disabled:opacity-50 ${
                isSaved
                  ? "bg-gold/[0.08] border-gold/30 text-gold"
                  : "bg-white border-border-light text-navy hover:border-gold/40"
              }`}
            >
              {isSaveLoading ? "Saving..." : isSaved ? "Saved" : "Save job"}
            </button>
          )}
          {showCandidateCtas && (
            <div className="flex items-center gap-2">
              {conversationId ? (
                <>
                  <InterestedStatus focusRef={interestSuccessRef} />
                  {actionsDisabled ? (
                    <span className="ml-auto inline-flex min-h-[44px] items-center text-[12px] font-semibold text-text-muted">
                      View conversation
                    </span>
                  ) : (
                    <Link
                      ref={conversationSuccessRef}
                      href={`/inbox/${conversationId}`}
                      className="ml-auto inline-flex min-h-[44px] items-center text-[12px] text-gold font-semibold no-underline hover:text-gold-light transition-colors focus:outline-2 focus:outline-offset-2 focus:outline-gold"
                    >
                      View Conversation
                    </Link>
                  )}
                </>
              ) : listing.response_mode === "message" ? (
                <>
                  {isInterested ? (
                    <InterestedStatus focusRef={interestSuccessRef} />
                  ) : (
                    <button
                      type="button"
                      onClick={onInterestClick}
                      disabled={actionsDisabled || isLoading}
                      className="min-h-[44px] px-4 py-2 rounded-sm text-[13px] font-semibold text-navy border-[1.5px] border-navy/18 bg-transparent cursor-pointer font-sans transition-all hover:border-navy disabled:opacity-50"
                    >
                      I&apos;m Interested
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={onMessageClick}
                    disabled={actionsDisabled || isLoading}
                    className="min-h-[44px] px-4 py-2 rounded-sm text-[13px] font-semibold bg-gold text-white border-none cursor-pointer font-sans transition-all hover:bg-gold-light disabled:opacity-50"
                  >
                    Message
                  </button>
                </>
              ) : isInterested ? (
                <InterestedStatus focusRef={interestSuccessRef} />
              ) : (
                <button
                  type="button"
                  onClick={onInterestClick}
                  disabled={actionsDisabled || isLoading}
                  className="min-h-[44px] px-4 py-2 rounded-sm text-[13px] font-semibold bg-gold text-white border-none cursor-pointer font-sans transition-all hover:bg-gold-light disabled:opacity-50"
                >
                  {isLoading ? "Registering..." : "I\u2019m Interested"}
                </button>
              )}
            </div>
          )}
          {showVisitorCtas && !actionsDisabled && (
            <div className="flex gap-2">
              <Link
                href={`/signup?intent=work&next=${encodeURIComponent(`/jobs/${listing.id}`)}`}
                className="inline-flex min-h-[44px] items-center px-4 py-2 rounded-sm text-[13px] font-semibold text-navy border-[1.5px] border-navy/18 no-underline font-sans transition-all hover:border-navy hover:bg-navy/[0.03] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
              >
                {listing.response_mode === "message"
                  ? "Sign Up to Apply"
                  : "Sign Up to Express Interest"}
              </Link>
            </div>
          )}
          {userRole === "employer" && !actionsDisabled && (
            <Link
              href={`/jobs/${listing.id}`}
              onClick={handleDetailLinkClick}
              className="inline-flex min-h-[44px] items-center text-[13px] text-gold font-semibold no-underline hover:text-gold-light focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
            >
              View Job &rarr;
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function InterestedStatus({
  focusRef,
}: {
  focusRef?: Ref<HTMLSpanElement>;
}) {
  return (
    <span
      ref={focusRef}
      role="status"
      tabIndex={focusRef ? -1 : undefined}
      className="inline-flex items-center gap-1 text-[12px] text-[#2E7D32] font-medium focus:outline-2 focus:outline-offset-2 focus:outline-gold"
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
      Interested
    </span>
  );
}
